"use client";

import { useRef } from "react";

interface SliderProps {
	value: number | [number, number];
	onChange: (value: number | [number, number]) => void;
	min: number;
	max: number;
	step?: number;
	isRange?: boolean;
	className?: string;
	trackClassName?: string;
	rangeClassName?: string;
	thumbClassName?: string;
	ariaLabel?: string | [string, string];
	disabled?: boolean;
	showTooltip?: boolean;
	tooltipFormatter?: (value: number) => string;
}

export function CustomSlider({
	value,
	onChange,
	min,
	max,
	step = 1,
	isRange = false,
	className = "",
	trackClassName = "bg-gray-300 relative grow rounded-full h-1",
	rangeClassName = "absolute bg-gray-500 rounded-full h-full",
	thumbClassName = "absolute block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 -ml-3",
	ariaLabel,
	disabled = false,
	showTooltip = false,
	tooltipFormatter = (val: number) => val.toString(),
}: SliderProps) {
	const sliderRef = useRef<HTMLDivElement>(null);
	const lastChangeRef = useRef<number | [number, number] | null>(null);

	// Normalize value to array format for consistent handling
	const normalizedValue = isRange ? (value as [number, number]) : [min, value as number];
	const [minValue, maxValue] = normalizedValue;

	// Calculate positions as percentages
	const minPosition = ((minValue - min) / (max - min)) * 100;
	const maxPosition = ((maxValue - min) / (max - min)) * 100;

	// Snap value to step
	const snapToStep = (val: number) => {
		const snapped = Math.round((val - min) / step) * step + min;
		return Math.max(min, Math.min(max, snapped));
	};

	// Convert mouse position to value
	const positionToValue = (clientX: number) => {
		if (!sliderRef.current) return min;

		const rect = sliderRef.current.getBoundingClientRect();
		const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		const rawValue = min + percentage * (max - min);
		return snapToStep(rawValue);
	};

	// Handle pointer events
	const handlePointerDown = (e: React.PointerEvent, thumbIndex?: number) => {
		if (disabled) return;

		const handlePointerMove = (e: PointerEvent | React.PointerEvent) => {
			const newValue = positionToValue(e.clientX);

			if (isRange) {
				const [currentMin, currentMax] = value as [number, number];
				let newMin = currentMin;
				let newMax = currentMax;

				if (thumbIndex === 0) {
					// Moving min thumb
					newMin = Math.min(newValue, currentMax);
				} else if (thumbIndex === 1) {
					// Moving max thumb
					newMax = Math.max(newValue, currentMin);
				} else {
					// Click on track - determine which thumb to move
					const distToMin = Math.abs(newValue - currentMin);
					const distToMax = Math.abs(newValue - currentMax);

					if (distToMin < distToMax) {
						newMin = Math.min(newValue, currentMax);
					} else {
						newMax = Math.max(newValue, currentMin);
					}
				}

				const newRangeValue: [number, number] = [newMin, newMax];

				// Prevent duplicate calls
				if (
					lastChangeRef.current &&
					Array.isArray(lastChangeRef.current) &&
					lastChangeRef.current[0] === newRangeValue[0] &&
					lastChangeRef.current[1] === newRangeValue[1]
				) {
					return;
				}

				lastChangeRef.current = newRangeValue;
				onChange(newRangeValue);
			} else {
				// Single value slider
				if (lastChangeRef.current === newValue) {
					return;
				}

				lastChangeRef.current = newValue;
				onChange(newValue);
			}
		};

		const handlePointerUp = () => {
			document.removeEventListener("pointermove", handlePointerMove);
			document.removeEventListener("pointerup", handlePointerUp);
		};

		// Handle initial click/touch
		handlePointerMove(e);

		document.addEventListener("pointermove", handlePointerMove);
		document.addEventListener("pointerup", handlePointerUp);
	};

	// Handle keyboard events
	const handleKeyDown = (e: React.KeyboardEvent, thumbIndex?: number) => {
		if (disabled) return;

		let newValue: number | undefined;
		const [currentMin, currentMax] = normalizedValue;
		const currentValue = isRange ? (thumbIndex === 0 ? currentMin : currentMax) : (value as number);

		switch (e.key) {
			case "ArrowRight":
			case "ArrowUp":
				newValue = snapToStep(currentValue + step);
				break;
			case "ArrowLeft":
			case "ArrowDown":
				newValue = snapToStep(currentValue - step);
				break;
			case "PageUp":
				newValue = snapToStep(currentValue + step * 10);
				break;
			case "PageDown":
				newValue = snapToStep(currentValue - step * 10);
				break;
			case "Home":
				newValue = min;
				break;
			case "End":
				newValue = max;
				break;
			default:
				return;
		}

		e.preventDefault();
		e.stopPropagation();

		if (isRange) {
			let newMin = currentMin;
			let newMax = currentMax;

			if (thumbIndex === 0) {
				// Moving min thumb
				newMin = Math.min(newValue, currentMax);
			} else {
				// Moving max thumb
				newMax = Math.max(newValue, currentMin);
			}

			onChange([newMin, newMax]);
		} else {
			onChange(newValue);
		}
	};

	return (
		<div
			ref={sliderRef}
			className={`relative flex items-center select-none touch-none w-full h-5 ${
				disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
			} ${className}`}
			onPointerDown={e => !isRange && handlePointerDown(e)}
		>
			{/* Track */}
			<div className={`w-full ${trackClassName}`}>
				{/* Range */}
				<div
					className={rangeClassName}
					style={{
						left: `${isRange ? minPosition : 0}%`,
						width: `${isRange ? maxPosition - minPosition : maxPosition}%`,
					}}
				/>
			</div>

			{/* Thumbs */}
			{isRange ? (
				<>
					{/* Min thumb */}
					<div
						className={thumbClassName}
						style={{ left: `${minPosition}%` }}
						onPointerDown={e => {
							e.stopPropagation();
							handlePointerDown(e, 0);
						}}
						onKeyDown={e => handleKeyDown(e, 0)}
						aria-label={Array.isArray(ariaLabel) ? ariaLabel[0] : `Minimum ${ariaLabel || "value"}`}
						role="slider"
						tabIndex={disabled ? -1 : 0}
						aria-valuenow={minValue}
						aria-valuemin={min}
						aria-valuemax={max}
						aria-orientation="horizontal"
					/>
					{/* Tooltip for min value */}
					{showTooltip && (
						<div
							className="absolute -top-8 bg-primary-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded pointer-events-none whitespace-nowrap"
							style={{ left: `${minPosition}%`, transform: "translateX(-50%)" }}
						>
							{tooltipFormatter(minValue)}
						</div>
					)}
					{/* Max thumb */}
					<div
						className={thumbClassName}
						style={{ left: `${maxPosition}%` }}
						onPointerDown={e => {
							e.stopPropagation();
							handlePointerDown(e, 1);
						}}
						onKeyDown={e => handleKeyDown(e, 1)}
						aria-label={Array.isArray(ariaLabel) ? ariaLabel[1] : `Maximum ${ariaLabel || "value"}`}
						role="slider"
						tabIndex={disabled ? -1 : 0}
						aria-valuenow={maxValue}
						aria-valuemin={min}
						aria-valuemax={max}
						aria-orientation="horizontal"
					/>
					{/* Tooltip for max value */}
					{showTooltip && (
						<div
							className="absolute -top-8 bg-primary-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded pointer-events-none whitespace-nowrap"
							style={{ left: `${maxPosition}%`, transform: "translateX(-50%)" }}
						>
							{tooltipFormatter(maxValue)}
						</div>
					)}
				</>
			) : (
				<>
					{/* Single thumb */}
					<div
						className={thumbClassName}
						style={{ left: `${maxPosition}%` }}
						onPointerDown={e => {
							e.stopPropagation();
							handlePointerDown(e);
						}}
						onKeyDown={e => handleKeyDown(e)}
						aria-label={Array.isArray(ariaLabel) ? ariaLabel[0] : ariaLabel}
						role="slider"
						tabIndex={disabled ? -1 : 0}
						aria-valuenow={maxValue}
						aria-valuemin={min}
						aria-valuemax={max}
						aria-orientation="horizontal"
					/>
					{/* Tooltip for single value */}
					{showTooltip && (
						<div
							className="absolute -top-4 bg-primary-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded pointer-events-none whitespace-nowrap"
							style={{ left: `${maxPosition}%`, transform: "translateX(-50%)" }}
						>
							{tooltipFormatter(maxValue)}
						</div>
					)}
				</>
			)}
		</div>
	);
}
